import moment from 'moment'
import { RootState } from '.'
import type { AnyVariantsAction } from '../actions/variants'
import type { Variant } from '../types'
import { ArrayUtils } from '../utils'

const initialState = {
    variants: [] as Variant[],
    error: undefined as string | undefined,
    isWorking: false,
    selectedVariant: {
        data: undefined as Variant | undefined,
        error: undefined as string | undefined,
        isWorking: false,
    },
}

export type VariantsState = typeof initialState

type RawVariant = Variant

const toStringArray = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.map(v => String(v).trim()).filter(Boolean)
    }

    if (typeof value === 'string') {
        return value.split(',').map(v => v.trim()).filter(Boolean)
    }

    return []
}

const toDateArray = (value: unknown): Date[] => {
    if (Array.isArray(value)) {
        return value.map(date => moment(date, ['YYYY-MM-DD', 'DD/MM/YYYY']).toDate())
    }

    if (typeof value === 'string') {
        return value.split(',')
            .map(date => date.trim())
            .filter(Boolean)
            .map(date => moment(date, ['YYYY-MM-DD', 'DD/MM/YYYY']).toDate())
    }

    return []
}

function formatVariantData(rawVariant: RawVariant): Variant {
    return {
        ...rawVariant,
        start_date: moment(rawVariant.start_date, ['YYYY-MM-DD', 'DD/MM/YYYY']).toDate(),
        end_date: moment(rawVariant.end_date, ['YYYY-MM-DD', 'DD/MM/YYYY']).toDate(),
        exceptions: {
            regions: [],
            skus: [],
            price: { less_than: null, greater_than: null },
            stores: [],
            uids: [],
            dates: [],
            ...rawVariant.exceptions,
            price: {
                less_than: rawVariant.exceptions?.price?.less_than ?? null,
                greater_than: rawVariant.exceptions?.price?.greater_than ?? null,
            },
            regions: toStringArray(rawVariant.exceptions?.regions),
            skus: toStringArray(rawVariant.exceptions?.skus),
            stores: toStringArray(rawVariant.exceptions?.stores),
            uids: toStringArray(rawVariant.exceptions?.uids),
            dates: toDateArray(rawVariant.exceptions?.dates),
        },
    }
}

function variantsReducer(state: VariantsState = initialState, { type, payload }: AnyVariantsAction) {
    switch (type) {
        case 'VARIANTS_FETCH##REQUEST':
            return {
                ...state,
                isWorking: true,
                error: undefined,
                variants: initialState.variants,
            }
        case 'VARIANTS_FETCH##SUCCESS':
            return {
                ...state,
                variants: payload.map(formatVariantData),
                isWorking: false,
                error: undefined,
            }
        case 'VARIANTS_FETCH##FAILURE':
            return {
                ...state,
                isWorking: false,
                error: payload.message,
            }
        case 'VARIANT_ID_FETCH##REQUEST':
            return {
                ...state,
                selectedVariant: {
                    data: undefined,
                    isWorking: true,
                    error: undefined,
                },
            }
        case 'VARIANT_ID_FETCH##SUCCESS':
            return {
                ...state,
                selectedVariant: {
                    data: formatVariantData(payload),
                    isWorking: false,
                    error: undefined,
                },
            }
        case 'VARIANT_ID_FETCH##FAILURE':
            return {
                ...state,
                selectedVariant: {
                    data: undefined,
                    isWorking: false,
                    error: payload.message,
                },
            }
        case 'VARIANT_DELETE##SUCCESS': {
            const index = state.variants.findIndex(variant => variant.id === payload)
            const updatedVariants = ArrayUtils.removeItem(state.variants, index)
            let { selectedVariant } = state

            if (selectedVariant.data?.id === payload) {
                selectedVariant = initialState.selectedVariant
            }

            return {
                ...state,
                variants: updatedVariants,
                selectedVariant,
            }
        }
        case 'VARIANT_UPDATE##SUCCESS': {
            const index = state.variants.findIndex(variant => variant.id === payload.id)
            const updatedVariants = ArrayUtils.insertItem(
                state.variants,
                index,
                formatVariantData(payload),
            )

            return {
                ...state,
                variants: updatedVariants,
            }
        }
        case 'VARIANT_CREATE##SUCCESS':
            return {
                ...state,
                variants: [
                    ...state.variants,
                    formatVariantData(payload),
                ],
            }
        default:
            return state
    }
}

function isWorking(state: RootState): boolean {
    return state.variants.isWorking
}

function selectVariants(state: RootState): Variant[] {
    return state.variants.variants
}

function isLoadingSelectedVariant(state: RootState): boolean {
    return state.variants.selectedVariant.isWorking
}

function selectedVariant(state: RootState): Variant | undefined {
    return state.variants.selectedVariant.data
}

export default variantsReducer

export {
    isWorking,
    selectVariants,
    selectedVariant,
    isLoadingSelectedVariant,
}
